import { useQuery } from '@tanstack/react-query';
import type { WorkflowToolCallPart } from '@cotera/sdk/core';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import {
  normalizeRun,
  EMPTY_WORKFLOW_RUN_COST,
  type WorkflowRunDetail,
} from '../workflow/workflow-run.types';

/** Reconcile cadence (ms) while the run is in flight, in case a broadcast is missed. */
const RUN_STATUS_POLL_MS = 3_000;

/**
 * Live status of the workflow run behind a `coco/workflow-*` tool call. The
 * pending tool payload is written once at dispatch and never updated mid-run,
 * so while the call is running this reads the run itself — kept fresh by the
 * workflow's `ResourceUpdated` broadcasts (query invalidation) plus a
 * reconcile poll. Returns `null` until the run row exists (or once the tool
 * call is no longer running).
 */
export function useWorkflowToolRunStatus(
  part: WorkflowToolCallPart | null
): string | null {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);

  const workflowId = part?.targetWorkflowId ?? null;
  const runId = part?.targetRunId ?? null;
  const enabled =
    part !== null &&
    part.phase === 'running' &&
    workflowId !== null &&
    runId !== null;

  const { data } = useQuery({
    queryKey: queryKeys.workflow.run({
      orgId,
      workflowId: workflowId ?? '',
      runId: runId ?? '',
    }),
    enabled,
    refetchInterval: enabled ? RUN_STATUS_POLL_MS : false,
    queryFn: async (): Promise<WorkflowRunDetail> => {
      if (workflowId === null || runId === null) {
        throw new Error('Missing workflow run reference');
      }
      const res = await client.resource.workflow.getRun({ workflowId, runId });
      if (res.isErr()) {
        // The run row may not exist yet right after dispatch; the poll retries.
        throw new Error('Workflow run not found');
      }
      const detail = res.value;
      // Same normalized shape WorkflowRunModel caches under this key.
      return {
        ...normalizeRun(detail),
        retriedByRunId: detail.retriedByRunId ?? null,
        input: detail.input ?? null,
        outputs: detail.outputs ?? [],
        cost: detail.cost ?? EMPTY_WORKFLOW_RUN_COST,
      };
    },
  });

  return enabled ? data?.status ?? null : null;
}
